import React, { useState, useEffect } from 'react';
import StatsCardsDashboard from '../../../components/shared/StatsCardsDashboard';
import debtService from '../../../services/debtService';
import {
  AccountBalance,
  CreditCard,
  Percent,
  Schedule
} from '@mui/icons-material';

/**
 * DebtStatsDashboard Component
 * 
 * Displays key debt statistics at the top of the debt section
 */
const DebtStatsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [debtStats, setDebtStats] = useState({
    totalDebt: 0,
    monthlyPayment: 0,
    weightedInterestRate: 0,
    debtToIncomeRatio: 0,
    payoffTimeline: 0,
    highestInterestDebt: null,
    lowestInterestDebt: null
  });

  useEffect(() => {
    const fetchDebtStats = async () => {
      try {
        setLoading(true);
        // Fetch actual debt statistics using the service
        const stats = await debtService.getDebtStatistics();
        setDebtStats(stats);
      } catch (error) {
        console.error('Error fetching debt statistics:', error);
        // Handle error state, using default values as fallback
      } finally {
        setLoading(false);
      }
    };

    fetchDebtStats();
  }, []);

  const formatCurrency = (value) => {
    // Handle cases where value is undefined, null, or not a number
    if (value === undefined || value === null || isNaN(value)) {
      return '$0.00';
    }
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value) => {
    // Handle cases where value is undefined, null, or not a number
    if (value === undefined || value === null || isNaN(value)) {
      return '0.0%';
    }
    return `${value.toFixed(1)}%`;
  };

  const debtMetrics = [
    {
      title: 'Total Debt',
      value: formatCurrency(debtStats.totalDebt),
      description: 'Overall debt balance',
      icon: AccountBalance,
      color: 'blue'
    },
    {
      title: 'Monthly Payment',
      value: formatCurrency(debtStats.monthlyPayment),
      description: formatPercentage(debtStats.debtToIncomeRatio) + ' of income',
      icon: CreditCard,
      color: 'red'
    },
    {
      title: 'Interest Rate',
      value: formatPercentage(debtStats.weightedInterestRate),
      description: 'Weighted average',
      icon: Percent,
      color: 'orange'
    },
    {
      title: 'Payoff Timeline',
      value: debtStats.payoffTimeline ? `${Math.ceil(debtStats.payoffTimeline)} months` : 'N/A',
      description: debtStats.highestInterestDebt 
        ? `Focus on ${debtStats.highestInterestDebt.name} first` 
        : 'No debts found',
      icon: Schedule,
      color: 'green'
    }
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-6">
      <StatsCardsDashboard
        title="Debt"
        metrics={debtMetrics}
        loading={loading}
      />
    </div>
  );
};

export default DebtStatsDashboard; 